/**
 * LedgerCombobox - Searchable combobox for ledger selection
 * Only renders matching options after user types, preventing DOM explosion
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { cn } from '../../lib/utils';
import { Check, ChevronsUpDown, Search } from 'lucide-react';

interface LedgerOption {
  name: string;
  group: string;
}

interface LedgerComboboxProps {
  value: string;
  onChange: (value: string) => void;
  ledgers: LedgerOption[];
  placeholder?: string;
  className?: string;
}

const MAX_VISIBLE = 50; // Only render top 50 matches

export const LedgerCombobox: React.FC<LedgerComboboxProps> = ({
  value,
  onChange,
  ledgers,
  placeholder = "Search ledger...",
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter ledgers based on search (case insensitive)
  const filteredLedgers = useMemo(() => {
    if (!search || search.length < 2) return [];
    const query = search.toLowerCase();
    return ledgers
      .filter(l => l.name.toLowerCase().includes(query))
      .slice(0, MAX_VISIBLE);
  }, [search, ledgers]);

  // Close dropdown when clicking outside and reset search
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch(''); // Reset search when closing
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle selection
  const handleSelect = (ledgerName: string) => {
    onChange(ledgerName);
    setSearch('');
    setOpen(false);
  };

  // Handle input focus
  const handleFocus = () => {
    setOpen(true);
    setSearch(''); // Clear search to show placeholder
  };

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  // When closed: show value. When open: show search text (placeholder shows value)
  const displayValue = open ? search : value;

  return (
    <div ref={containerRef} className={cn("relative", className)} title={value}>
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          placeholder={open ? `Search: ${value || placeholder}` : (value || placeholder)}
          className={cn(
            "w-full h-8 pl-7 pr-8 text-sm rounded-md border border-input bg-background",
            "focus:outline-none focus:ring-1 focus:ring-ring",
            "text-foreground truncate",
            open && !search && "placeholder:text-foreground/70"
          )}
          title={value}
        />
        <ChevronsUpDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-60 overflow-auto">
          {search.length < 2 ? (
            <div className="p-2 text-sm text-muted-foreground text-center">
              Type 2+ characters to search...
            </div>
          ) : filteredLedgers.length === 0 ? (
            <div className="p-2 text-sm text-muted-foreground text-center">
              No ledgers found
            </div>
          ) : (
            <>
              {filteredLedgers.map((ledger) => (
                <div
                  key={ledger.name}
                  onClick={() => handleSelect(ledger.name)}
                  className={cn(
                    "flex items-center px-2 py-1.5 text-sm cursor-pointer hover:bg-accent",
                    value === ledger.name && "bg-accent"
                  )}
                >
                  <Check className={cn(
                    "w-3 h-3 mr-2 flex-shrink-0",
                    value === ledger.name ? "opacity-100" : "opacity-0"
                  )} />
                  <span className="truncate">{ledger.name}</span>
                  <span className="ml-auto text-xs text-muted-foreground truncate max-w-20">
                    {ledger.group}
                  </span>
                </div>
              ))}
              {filteredLedgers.length === MAX_VISIBLE && (
                <div className="p-1 text-xs text-muted-foreground text-center border-t">
                  Showing first {MAX_VISIBLE} matches...
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default LedgerCombobox;
